import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Card,
    CardContent,
    TextField,
    Button,
    Alert,
    CircularProgress,
    Avatar,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton as MuiIconButton,
    LinearProgress,
    InputAdornment
} from '@mui/material';
import {
    Person,
    Email,
    Phone,
    Lock,
    Security,
    Visibility,
    VisibilityOff,
    PhotoCamera,
    Delete
} from '@mui/icons-material';
import { API_ENDPOINTS } from '@services/database';
import { Layout } from '@layout/Layout';
import { useAuth } from '@store/authStore';
import { useAppTheme } from '@contexts/ThemeContext';
import { useTheme } from '@mui/material/styles';
import httpClient from '@utils/httpClient';
import NotificationPreferences from '@pages/profile/components/NotificationPreferences';

const HRProfileSettings: React.FC = () => {
    const { user, login, logout } = useAuth();
    const theme = useTheme();
    const { isDarkMode } = useAppTheme();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState('');
    const [error, setError] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [openPasswordDialog, setOpenPasswordDialog] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [isUploading, setIsUploading] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const [profileData, setProfileData] = useState({
        name: user?.name || '',
        email: user?.email || '',
        contact: user?.contact || '',
        profilePhoto: user?.profilePhoto || '',
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
    });

    // Debug: Log initial user data
    useEffect(() => {
        console.log('🔍 Initial user data from auth store:', user);
        console.log('🔍 Initial profileData state:', {
            name: user?.name || '',
            email: user?.email || '',
            contact: user?.contact || '',
            profilePhoto: user?.profilePhoto || ''
        });
    }, []);

    // Debug: Log profileData changes
    useEffect(() => {
        console.log('📊 ProfileData state changed:', {
            name: profileData.name,
            email: profileData.email,
            contact: profileData.contact,
            profilePhoto: profileData.profilePhoto
        });
        console.log('📊 Full profileData object:', profileData);
    }, [profileData.profilePhoto]);


    useEffect(() => {
        if (user) {
            setProfileData({
                name: user.name || '',
                email: user.email || '',
                contact: user.contact || '',
                profilePhoto: user.profilePhoto || '',
                currentPassword: '',
                newPassword: '',
                confirmPassword: ''
            });
        }
    }, [user]);

    // Load current user profile data from database
    useEffect(() => {
        const loadUserProfile = async () => {
            if (user?.id) {
                try {
                    console.log('🔍 Current user from auth store:', user);
                    const userData = await httpClient.get(`/users/${user.id}`);
                    console.log('📊 Loaded user profile from database:', userData);

                    if (userData.user) {
                        const fullPhotoUrl = userData.user.profile_photo
                            ? (userData.user.profile_photo.startsWith('http')
                                ? userData.user.profile_photo
                                : `http://localhost:5000${userData.user.profile_photo}`)
                            : '';

                        console.log('🖼️ Full photo URL from database:', fullPhotoUrl);

                        setProfileData(prev => ({
                            ...prev,
                            name: userData.user.name || prev.name,
                            email: userData.user.email || prev.email,
                            contact: userData.user.contact || prev.contact,
                            profilePhoto: fullPhotoUrl
                        }));

                        // Also update the auth store with the correct profile photo
                        // Preserve existing user data from auth store, only update specific fields
                        const userDataForAuth = {
                            ...user, // Preserve ALL existing fields including role
                            ...userData.user, // Override with backend data
                            profilePhoto: fullPhotoUrl, // Ensure profilePhoto is set
                            role: user.role // ALWAYS preserve the role from auth store
                        };
                        login(userDataForAuth, localStorage.getItem('authToken') || '');
                    }
                } catch (error) {
                    console.error('❌ Error loading user profile:', error);
                }
            }
        };

        loadUserProfile();
    }, [user?.id]);

    const handleProfileUpdate = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        setSuccess('');

        try {
            const token = localStorage.getItem('authToken');
            console.log('🔍 Profile Update Debug:', {
                userId: user?.id,
                token: token ? 'Token present' : 'No token',
                apiUrl: `${API_ENDPOINTS.USERS}/${user?.id}`,
                profileData
            });

            // Extract relative path from full URL if it's a full URL
            let profilePhotoPath = profileData.profilePhoto;
            if (profileData.profilePhoto && profileData.profilePhoto.startsWith('http')) {
                const url = new URL(profileData.profilePhoto);
                profilePhotoPath = url.pathname;
            }

            const updatedUser = await httpClient.put(`/users/${user?.id}`, {
                name: profileData.name,
                email: profileData.email,
                contact: profileData.contact,
                profile_photo: profilePhotoPath
            });

            console.log('📡 Profile Update Response:', updatedUser);
            console.log('✅ Profile Update Success:', updatedUser);
            // Update the auth store with new user data - convert profile_photo to profilePhoto
            // Convert relative path back to full URL for frontend display
            const fullPhotoUrl = updatedUser.user.profile_photo
                ? (updatedUser.user.profile_photo.startsWith('http')
                    ? updatedUser.user.profile_photo
                    : `http://localhost:5000${updatedUser.user.profile_photo}`)
                : '';

            const userData = {
                ...user, // Preserve ALL existing fields including role
                ...updatedUser.user,
                profilePhoto: fullPhotoUrl,
                role: user?.role // ALWAYS preserve the role from auth store
            };
            login(userData, token || '');
            setSuccess('Profile updated successfully!');
        } catch (error) {
            console.error('❌ Profile Update Exception:', error);
            setError(`Failed to update profile: ${error instanceof Error ? error.message : 'Unknown error'}`);
        } finally {
            setLoading(false);
            setTimeout(() => setSuccess(''), 3000);
            setTimeout(() => setError(''), 3000);
        }
    };

    const handlePasswordChange = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        setSuccess('');

        if (profileData.newPassword !== profileData.confirmPassword) {
            setError('New passwords do not match');
            setLoading(false);
            return;
        }

        try {
            const token = localStorage.getItem('authToken');
            console.log('🔐 Password Change Debug:', {
                userId: user?.id,
                token: token ? 'Token present' : 'No token',
                currentPassword: profileData.currentPassword ? 'Present' : 'Missing',
                newPassword: profileData.newPassword ? 'Present' : 'Missing'
            });

            // Call backend API to change password
            const response = await httpClient.post('/auth/change-password', {
                currentPassword: profileData.currentPassword,
                newPassword: profileData.newPassword
            });

            console.log('🔐 Password Change Response:', response);

            setSuccess('Password changed successfully!');
            setOpenPasswordDialog(false);
            setProfileData(prev => ({
                ...prev,
                currentPassword: '',
                newPassword: '',
                confirmPassword: ''
            }));

            // Logout user after password change since all sessions are invalidated
            setTimeout(() => {
                logout();
            }, 2000);
        } catch (error) {
            console.error('❌ Password Change Exception:', error);
            setError('Failed to change password');
            setTimeout(() => setError(''), 3000);
        } finally {
            setLoading(false);
            setTimeout(() => setSuccess(''), 8080);
            setTimeout(() => setError(''), 8080);
            setTimeout(() => setSuccess(''), 3000);
        }
    };

    const handleForgotPassword = () => {
        setOpenPasswordDialog(false);
        logout();
        navigate('/reset-password');
    };

    const handlePhotoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        console.log('📸 Photo Upload Debug:', {
            fileName: file.name,
            fileSize: file.size,
            fileType: file.type,
            userId: user?.id
        });

        // Validate file type
        if (!file.type.startsWith('image/')) {
            setError('Please select a valid image file');
            return;
        }

        // Validate file size (max 5MB)
        if (file.size > 5 * 1024 * 1024) {
            setError('File size must be less than 5MB');
            return;
        }

        setIsUploading(true);
        setUploadProgress(0);
        setError('');

        try {
            const formData = new FormData();
            formData.append('profile_photo', file);

            console.log('📤 Uploading photo to:', `${API_ENDPOINTS.USERS}/${user?.id}/upload-photo`);

            const result = await httpClient.post(`/users/${user?.id}/upload-photo`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });

            console.log('📡 Photo Upload Response:', result);
            console.log('✅ Photo Upload Success:', result);

            // Convert relative path to full URL
            const fullPhotoUrl = result.profile_photo.startsWith('http')
                ? result.profile_photo
                : `http://localhost:5000${result.profile_photo}`;

            console.log('🖼️ Full photo URL:', fullPhotoUrl);
            console.log('🖼️ Original profile_photo from backend:', result.profile_photo);

            // Update local state
            setProfileData(prev => ({ ...prev, profilePhoto: fullPhotoUrl }));

            // Update the user data in auth store and localStorage
            if (user?.id) {
                console.log('🔍 Current user object before update:', user);

                const updatedUser = {
                    ...user,
                    profilePhoto: fullPhotoUrl,
                    role: user?.role // ALWAYS preserve the role from auth store
                };

                console.log('🔄 Updating auth store with new photo:', fullPhotoUrl);
                console.log('🔍 Updated user object:', updatedUser);

                // Update auth store
                login(updatedUser, localStorage.getItem('authToken') || '');

                // Update localStorage directly
                localStorage.setItem('user', JSON.stringify(updatedUser));

                // Dispatch custom event to notify other components
                window.dispatchEvent(new CustomEvent('userUpdated'));

                // Force a small delay to ensure all updates are processed
                setTimeout(() => {
                    console.log('🔄 Forcing component refresh after photo update');
                    window.dispatchEvent(new CustomEvent('userUpdated'));
                }, 100);

                console.log('✅ Auth store updated successfully');
                console.log('💾 Updated localStorage user data:', JSON.parse(localStorage.getItem('user') || '{}'));
            }

            console.log('💾 Updated user data in store and localStorage');

            setSuccess('Profile photo updated successfully!');
        } catch (error) {
            console.error('❌ Photo Upload Exception:', error);
            setError(`Failed to upload photo: ${error instanceof Error ? error.message : 'Unknown error'}`);
        } finally {
            setIsUploading(false);
            setUploadProgress(0);
            setTimeout(() => setSuccess(''), 3000);
            setTimeout(() => setError(''), 3000);
        }
    };

    const handleRemovePhoto = async () => {
        setLoading(true);
        setError('');

        try {
            await httpClient.delete(`/users/${user?.id}/remove-photo`);
            setProfileData(prev => ({ ...prev, profilePhoto: '' }));

            // Update the user data in auth store and localStorage
            if (user?.id) {
                const updatedUser = {
                    ...user,
                    profilePhoto: '',
                    role: user?.role // ALWAYS preserve the role from auth store
                };

                // Update auth store
                login(updatedUser, localStorage.getItem('authToken') || '');

                // Update localStorage directly
                localStorage.setItem('user', JSON.stringify(updatedUser));
            }

            console.log('🗑️ Removed profile photo from store and localStorage');
            setSuccess('Profile photo removed successfully!');
        } catch (error) {
            setError('Failed to remove photo');
        } finally {
            setLoading(false);
            setTimeout(() => setSuccess(''), 3000);
            setTimeout(() => setError(''), 3000);
        }
    };


    return (
        <Layout>
            <Box sx={{
                p: { xs: 2, sm: 3 },
                color: theme.palette.text.primary,
                minHeight: '100vh',
                background: isDarkMode
                    ? 'linear-gradient(135deg, #1a1f3a 0%, #20232a 100%)'
                    : theme.palette.background.default
            }}>
                {/* Header */}
                <Box mb={4}>
                    <Typography variant="h4" component="h1" sx={{
                        color: theme.palette.text.primary,
                        mb: 1,
                        fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' },
                        fontWeight: { xs: 600, sm: 700 }
                    }}>
                        HR Profile Settings
                    </Typography>
                    <Typography variant="body1" sx={{
                        color: theme.palette.text.secondary,
                        fontSize: { xs: '0.875rem', sm: '1rem' }
                    }}>
                        Manage your HR profile information and security settings
                    </Typography>
                </Box>

                {/* Alerts */}
                {error && (
                    <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
                        {error}
                    </Alert>
                )}
                {success && (
                    <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
                        {success}
                    </Alert>
                )}

                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, 1fr)' },
                        gap: { xs: 2, sm: 3 }
                    }}
                >
                    {/* Profile Information Card */}
                    <Box>
                        <Card sx={{
                            background: isDarkMode
                                ? 'linear-gradient(135deg, #1a1f3a 0%, #20232a 100%)'
                                : 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
                            border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.2)' : `1px solid ${theme.palette.divider}`,
                            borderRadius: 3,
                            boxShadow: isDarkMode
                                ? '0 8px 32px rgba(0, 0, 0, 0.3)'
                                : '0 4px 20px rgba(0, 0, 0, 0.08)',
                            overflow: 'hidden',
                            transition: 'all 0.3s ease',
                            height: '100%', // Make card take full height
                            display: 'flex',
                            flexDirection: 'column',
                            '&:hover': {
                                boxShadow: isDarkMode
                                    ? '0 12px 40px rgba(0, 0, 0, 0.4)'
                                    : '0 8px 30px rgba(0, 0, 0, 0.12)',
                                transform: 'translateY(-2px)'
                            }
                        }}>
                            <CardContent sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
                                {/* Card Header */}
                                <Box sx={{ mb: 4 }}>
                                    <Typography variant="h5" sx={{
                                        color: theme.palette.text.primary,
                                        fontWeight: 700,
                                        fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.75rem' },
                                        mb: 1
                                    }}>
                                        Profile Information
                                    </Typography>
                                    <Typography variant="body2" sx={{
                                        color: theme.palette.text.secondary,
                                        fontSize: { xs: '0.875rem', sm: '1rem' }
                                    }}>
                                        Manage your personal information and profile settings
                                    </Typography>
                                </Box>

                                {/* Profile Avatar Section */}
                                <Box sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    mb: 4,
                                    p: 3,
                                    backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)',
                                    borderRadius: 2,
                                    border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.05)' : '1px solid rgba(0, 0, 0, 0.05)'
                                }}>
                                    <Box position="relative" sx={{ mr: 3 }}>
                                        <Avatar
                                            key={`profile-avatar-${profileData.profilePhoto || 'no-photo'}`}
                                            sx={{
                                                width: 100,
                                                height: 100,
                                                backgroundColor: profileData.profilePhoto ? 'transparent' : '#1976d2',
                                                fontSize: '2.5rem',
                                                border: isDarkMode ? '3px solid rgba(255, 255, 255, 0.1)' : '3px solid rgba(0, 0, 0, 0.1)',
                                                transition: 'all 0.3s ease',
                                                '&:hover': {
                                                    transform: 'scale(1.05)',
                                                    boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)'
                                                }
                                            }}
                                            src={profileData.profilePhoto}
                                        >
                                            {!profileData.profilePhoto && (user?.name?.charAt(0).toUpperCase() || 'U')}
                                        </Avatar>

                                        {/* Camera Icon Overlay */}
                                        <IconButton
                                            sx={{
                                                position: 'absolute',
                                                bottom: 0,
                                                right: 0,
                                                backgroundColor: '#1976d2',
                                                color: 'white',
                                                width: 36,
                                                height: 36,
                                                border: '3px solid white',
                                                transition: 'all 0.3s ease',
                                                '&:hover': {
                                                    backgroundColor: '#1565c0',
                                                    transform: 'scale(1.1)',
                                                    boxShadow: '0 4px 15px rgba(25, 118, 210, 0.4)'
                                                }
                                            }}
                                            onClick={() => fileInputRef.current?.click()}
                                            disabled={isUploading}
                                        >
                                            <PhotoCamera sx={{ fontSize: 18 }} />
                                        </IconButton>

                                        {/* Remove Photo Button */}
                                        {profileData.profilePhoto && (
                                            <IconButton
                                                sx={{
                                                    position: 'absolute',
                                                    top: 0,
                                                    right: 0,
                                                    backgroundColor: '#f44336',
                                                    color: 'white',
                                                    width: 28,
                                                    height: 28,
                                                    border: '2px solid white',
                                                    transition: 'all 0.3s ease',
                                                    '&:hover': {
                                                        backgroundColor: '#d32f2f',
                                                        transform: 'scale(1.1)',
                                                        boxShadow: '0 4px 15px rgba(244, 67, 54, 0.4)'
                                                    }
                                                }}
                                                onClick={handleRemovePhoto}
                                                disabled={loading}
                                            >
                                                <Delete sx={{ fontSize: 16 }} />
                                            </IconButton>
                                        )}
                                    </Box>

                                    <Box flex={1}>
                                        <Typography variant="h6" sx={{
                                            color: theme.palette.text.primary,
                                            fontWeight: 600,
                                            fontSize: '1.25rem',
                                            mb: 1
                                        }}>
                                            {user?.name || 'User'}
                                        </Typography>
                                        <Typography variant="body2" sx={{
                                            color: theme.palette.text.secondary,
                                            mb: 2
                                        }}>
                                            Click the camera icon to change your profile photo
                                        </Typography>

                                        <input
                                            ref={fileInputRef}
                                            type="file"
                                            accept="image/*"
                                            onChange={handlePhotoUpload}
                                            style={{ display: 'none' }}
                                        />

                                        {isUploading && (
                                            <Box sx={{ mt: 2 }}>
                                                <LinearProgress
                                                    variant="determinate"
                                                    value={uploadProgress}
                                                    sx={{
                                                        height: 6,
                                                        borderRadius: 3,
                                                        backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
                                                        '& .MuiLinearProgress-bar': {
                                                            backgroundColor: '#1976d2',
                                                            borderRadius: 3
                                                        }
                                                    }}
                                                />
                                                <Typography variant="caption" sx={{
                                                    color: theme.palette.text.secondary,
                                                    mt: 1,
                                                    display: 'block'
                                                }}>
                                                    Uploading... {uploadProgress}%
                                                </Typography>
                                            </Box>
                                        )}
                                    </Box>
                                </Box>

                                {/* Editable Profile Fields */}
                                <Box sx={{ mb: 3 }}>
                                    <TextField
                                        fullWidth
                                        label="Full Name"
                                        value={profileData.name}
                                        onChange={(e) => setProfileData(prev => ({ ...prev, name: e.target.value }))}
                                        margin="normal"
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <Person sx={{ color: theme.palette.text.secondary }} />
                                                </InputAdornment>
                                            ),
                                        }}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: 2,
                                                backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
                                                '& fieldset': {
                                                    borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.15)',
                                                    borderWidth: 1.5
                                                },
                                                '&:hover fieldset': {
                                                    borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.4)' : 'rgba(0, 0, 0, 0.3)',
                                                },
                                                '&.Mui-focused fieldset': {
                                                    borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.7)' : theme.palette.text.primary,
                                                    borderWidth: 2
                                                },
                                            },
                                            '& .MuiInputLabel-root': {
                                                color: theme.palette.text.secondary,
                                                fontWeight: 500
                                            },
                                            '& .MuiInputLabel-root.Mui-focused': {
                                                color: isDarkMode ? 'rgba(255, 255, 255, 0.9)' : theme.palette.text.primary
                                            },
                                        }}
                                    />

                                    <TextField
                                        fullWidth
                                        label="Email Address"
                                        type="email"
                                        value={profileData.email}
                                        onChange={(e) => setProfileData(prev => ({ ...prev, email: e.target.value }))}
                                        margin="normal"
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <Email sx={{ color: theme.palette.text.secondary }} />
                                                </InputAdornment>
                                            ),
                                        }}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: 2,
                                                backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
                                                '& fieldset': {
                                                    borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.15)',
                                                    borderWidth: 1.5
                                                },
                                                '&:hover fieldset': {
                                                    borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.4)' : 'rgba(0, 0, 0, 0.3)',
                                                },
                                                '&.Mui-focused fieldset': {
                                                    borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.7)' : theme.palette.text.primary,
                                                    borderWidth: 2
                                                },
                                            },
                                            '& .MuiInputLabel-root': {
                                                color: theme.palette.text.secondary,
                                                fontWeight: 500
                                            },
                                            '& .MuiInputLabel-root.Mui-focused': {
                                                color: isDarkMode ? 'rgba(255, 255, 255, 0.9)' : theme.palette.text.primary
                                            },
                                        }}
                                    />

                                    <TextField
                                        fullWidth
                                        label="Contact Number"
                                        value={profileData.contact}
                                        onChange={(e) => setProfileData(prev => ({ ...prev, contact: e.target.value }))}
                                        margin="normal"
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <Phone sx={{ color: theme.palette.text.secondary }} />
                                                </InputAdornment>
                                            ),
                                        }}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: 2,
                                                backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
                                                '& fieldset': {
                                                    borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.15)',
                                                    borderWidth: 1.5
                                                },
                                                '&:hover fieldset': {
                                                    borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.4)' : 'rgba(0, 0, 0, 0.3)',
                                                },
                                                '&.Mui-focused fieldset': {
                                                    borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.7)' : theme.palette.text.primary,
                                                    borderWidth: 2
                                                },
                                            },
                                            '& .MuiInputLabel-root': {
                                                color: theme.palette.text.secondary,
                                                fontWeight: 500
                                            },
                                            '& .MuiInputLabel-root.Mui-focused': {
                                                color: isDarkMode ? 'rgba(255, 255, 255, 0.9)' : theme.palette.text.primary
                                            },
                                        }}
                                    />
                                </Box>

                                {/* Save Button */}
                                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                                    <Button
                                        variant="contained"
                                        onClick={handleProfileUpdate}
                                        disabled={loading}
                                        sx={{
                                            px: 4,
                                            py: 1.5,
                                            borderRadius: 2,
                                            fontSize: '1rem',
                                            fontWeight: 600,
                                            textTransform: 'none',
                                            background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                                            boxShadow: '0 4px 15px rgba(25, 118, 210, 0.3)',
                                            transition: 'all 0.3s ease',
                                            '&:hover': {
                                                background: 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)',
                                                boxShadow: '0 6px 20px rgba(25, 118, 210, 0.4)',
                                                transform: 'translateY(-2px)'
                                            },
                                            '&:disabled': {
                                                background: 'rgba(0, 0, 0, 0.12)',
                                                color: 'rgba(0, 0, 0, 0.26)',
                                                boxShadow: 'none'
                                            }
                                        }}
                                    >
                                        {loading ? (
                                            <>
                                                <CircularProgress size={20} sx={{ mr: 1 }} />
                                                Saving...
                                            </>
                                        ) : (
                                            'Save Profile'
                                        )}
                                    </Button>
                                </Box>
                            </CardContent>
                        </Card>
                    </Box>

                    {/* Security Settings Card */}
                    <Box>
                        <Card sx={{
                            background: isDarkMode
                                ? 'linear-gradient(135deg, #1a1f3a 0%, #20232a 100%)'
                                : 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
                            border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.2)' : `1px solid ${theme.palette.divider}`,
                            borderRadius: 3,
                            boxShadow: isDarkMode
                                ? '0 8px 32px rgba(0, 0, 0, 0.3)'
                                : '0 4px 20px rgba(0, 0, 0, 0.08)',
                            overflow: 'hidden',
                            transition: 'all 0.3s ease',
                            height: '100%', // Make card take full height
                            display: 'flex',
                            flexDirection: 'column',
                            '&:hover': {
                                boxShadow: isDarkMode
                                    ? '0 12px 40px rgba(0, 0, 0, 0.4)'
                                    : '0 8px 30px rgba(0, 0, 0, 0.12)',
                                transform: 'translateY(-2px)'
                            }
                        }}>
                            <CardContent sx={{
                                p: { xs: 2, sm: 3, md: 4 },
                                flex: 1,
                                display: 'flex',
                                flexDirection: 'column'
                            }}>
                                {/* Card Header */}
                                <Box sx={{ mb: 4 }}>
                                    <Box display="flex" alignItems="center" mb={2}>
                                        <Security sx={{
                                            mr: 2,
                                            color: '#f44336',
                                            fontSize: 28
                                        }} />
                                        <Typography variant="h5" sx={{
                                            color: theme.palette.text.primary,
                                            fontWeight: 700,
                                            fontSize: '1.75rem'
                                        }}>
                                            Security Settings
                                        </Typography>
                                    </Box>
                                    <Typography variant="body2" sx={{
                                        color: theme.palette.text.secondary,
                                        fontSize: '1rem'
                                    }}>
                                        Manage your account security and privacy preferences
                                    </Typography>
                                </Box>

                                {/* Change Password Section */}
                                <Box sx={{
                                    mb: 4,
                                    p: 3,
                                    backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)',
                                    borderRadius: 2,
                                    border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.05)' : '1px solid rgba(0, 0, 0, 0.05)'
                                }}>
                                    <Box display="flex" alignItems="center" mb={2}>
                                        <Lock sx={{
                                            mr: 2,
                                            color: '#1976d2',
                                            fontSize: 24
                                        }} />
                                        <Typography variant="h6" sx={{
                                            color: theme.palette.text.primary,
                                            fontWeight: 600
                                        }}>
                                            Password
                                        </Typography>
                                    </Box>
                                    <Typography variant="body2" sx={{
                                        color: theme.palette.text.secondary,
                                        mb: 3
                                    }}>
                                        Change your password to keep your account secure
                                    </Typography>
                                    <Button
                                        variant="contained"
                                        startIcon={<Lock />}
                                        onClick={() => setOpenPasswordDialog(true)}
                                        sx={{
                                            px: 3,
                                            py: 1.5,
                                            borderRadius: 2,
                                            fontSize: '1rem',
                                            fontWeight: 600,
                                            textTransform: 'none',
                                            color: 'white',
                                            backgroundColor: '#1976d2',
                                            transition: 'all 0.3s ease',
                                            '&:hover': {
                                                backgroundColor: '#1565c0',
                                                transform: 'translateY(-1px)',
                                                boxShadow: '0 4px 12px rgba(25, 118, 210, 0.15)'
                                            },
                                            '&:focus': {
                                                outline: 'none'
                                            }
                                        }}
                                    >
                                        Change Password
                                    </Button>
                                </Box>

                                {/* Notification Preferences */}
                                <Box sx={{ mb: 4, flex: 1 }}>
                                    <NotificationPreferences />
                                </Box>
                            </CardContent>
                        </Card>
                    </Box>
                </Box>

                {/* Password Change Dialog */}
                <Dialog
                    open={openPasswordDialog}
                    onClose={() => setOpenPasswordDialog(false)}
                    maxWidth="sm"
                    fullWidth
                    PaperProps={{
                        sx: {
                            background: isDarkMode
                                ? 'linear-gradient(135deg, #1a1f3a 0%, #20232a 100%)'
                                : 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
                            backdropFilter: 'blur(10px)',
                            color: theme.palette.text.primary,
                            borderRadius: { xs: 2, sm: 3 },
                            border: isDarkMode
                                ? '1px solid rgba(255, 255, 255, 0.2)'
                                : `1px solid ${theme.palette.divider}`
                        }
                    }}
                >
                    <DialogTitle sx={{ color: theme.palette.text.primary }}>
                        Change Password
                    </DialogTitle>
                    <form onSubmit={handlePasswordChange}>
                        <DialogContent>
                            <TextField
                                fullWidth
                                label="Current Password"
                                type={showPassword ? 'text' : 'password'}
                                value={profileData.currentPassword}
                                onChange={(e) => setProfileData(prev => ({ ...prev, currentPassword: e.target.value }))}
                                margin="normal"
                                required
                                InputLabelProps={{
                                    shrink: true
                                }}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <MuiIconButton
                                                onClick={() => setShowPassword(!showPassword)}
                                                edge="end"
                                            >
                                                {showPassword ? <VisibilityOff /> : <Visibility />}
                                            </MuiIconButton>
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        color: theme.palette.text.primary,
                                        backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : theme.palette.action.hover,
                                        '& fieldset': {
                                            borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.3)' : theme.palette.text.primary,
                                            borderWidth: 1.5
                                        },
                                        '&:hover fieldset': {
                                            borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.5)' : theme.palette.text.primary
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.7)' : theme.palette.text.primary,
                                            borderWidth: 2
                                        },
                                    },
                                    '& .MuiInputLabel-root': {
                                        color: theme.palette.text.secondary
                                    },
                                    '& .MuiInputLabel-root.Mui-focused': {
                                        color: isDarkMode ? 'rgba(255, 255, 255, 0.9)' : theme.palette.text.primary
                                    },
                                }}
                            />

                            {/* Don't remember current password link - right below Current Password field */}
                            <Box sx={{ textAlign: 'right', mt: 1, mb: 2 }}>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        color: '#1976d2',
                                        cursor: 'pointer',
                                        textDecoration: 'underline',
                                        fontSize: '0.875rem',
                                        '&:hover': {
                                            color: '#1565c0',
                                            textDecoration: 'none'
                                        }
                                    }}
                                    onClick={handleForgotPassword}
                                >
                                    Don't remember current password?
                                </Typography>
                            </Box>

                            <TextField
                                fullWidth
                                label="New Password"
                                type={showNewPassword ? 'text' : 'password'}
                                value={profileData.newPassword}
                                onChange={(e) => setProfileData(prev => ({ ...prev, newPassword: e.target.value }))}
                                margin="normal"
                                required
                                InputLabelProps={{
                                    shrink: true
                                }}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <MuiIconButton
                                                onClick={() => setShowNewPassword(!showNewPassword)}
                                                edge="end"
                                            >
                                                {showNewPassword ? <VisibilityOff /> : <Visibility />}
                                            </MuiIconButton>
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        color: theme.palette.text.primary,
                                        backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : theme.palette.action.hover,
                                        '& fieldset': {
                                            borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.3)' : theme.palette.text.primary,
                                            borderWidth: 1.5
                                        },
                                        '&:hover fieldset': {
                                            borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.5)' : theme.palette.text.primary
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.7)' : theme.palette.text.primary,
                                            borderWidth: 2
                                        },
                                    },
                                    '& .MuiInputLabel-root': {
                                        color: theme.palette.text.secondary
                                    },
                                    '& .MuiInputLabel-root.Mui-focused': {
                                        color: isDarkMode ? 'rgba(255, 255, 255, 0.9)' : theme.palette.text.primary
                                    },
                                }}
                            />

                            <TextField
                                fullWidth
                                label="Confirm New Password"
                                type={showConfirmPassword ? 'text' : 'password'}
                                value={profileData.confirmPassword}
                                onChange={(e) => setProfileData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                                margin="normal"
                                required
                                InputLabelProps={{
                                    shrink: true
                                }}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <MuiIconButton
                                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                                edge="end"
                                            >
                                                {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                                            </MuiIconButton>
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        color: theme.palette.text.primary,
                                        backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : theme.palette.action.hover,
                                        '& fieldset': {
                                            borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.3)' : theme.palette.text.primary,
                                            borderWidth: 1.5
                                        },
                                        '&:hover fieldset': {
                                            borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.5)' : theme.palette.text.primary
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.7)' : theme.palette.text.primary,
                                            borderWidth: 2
                                        },
                                    },
                                    '& .MuiInputLabel-root': {
                                        color: theme.palette.text.secondary
                                    },
                                    '& .MuiInputLabel-root.Mui-focused': {
                                        color: isDarkMode ? 'rgba(255, 255, 255, 0.9)' : theme.palette.text.primary
                                    },
                                }}
                            />
                        </DialogContent>
                        <DialogActions>
                            <Button
                                onClick={() => setOpenPasswordDialog(false)}
                                sx={{
                                    color: isDarkMode ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)',
                                    backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.04)',
                                    border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.3)' : '1px solid rgba(0, 0, 0, 0.23)',
                                    '&:hover': {
                                        backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.08)',
                                        borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.4)',
                                    },
                                }}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                variant="contained"
                                disabled={loading}
                                sx={{
                                    color: 'white',
                                    backgroundColor: '#1976d2',
                                    '&:hover': {
                                        backgroundColor: '#1565c0'
                                    },
                                    '&:focus': {
                                        outline: 'none'
                                    },
                                    '&:disabled': {
                                        backgroundColor: 'rgba(0, 0, 0, 0.12)',
                                        color: 'rgba(0, 0, 0, 0.26)'
                                    }
                                }}
                            >
                                {loading ? <CircularProgress size={20} /> : 'Change Password'}
                            </Button>
                        </DialogActions>
                    </form>
                </Dialog>
            </Box>
        </Layout>
    );
};

export default HRProfileSettings;
